import cx from 'classnames';
import { FC, ReactNode, RefObject, useMemo } from 'react';

import { t } from '@core/i18n';
import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { Loading } from '@shared/components/loading';
import { ArrowRightIcon } from '@shared/icons/arrow-right';
import { BellIcon } from '@shared/icons/bell';
import { ChartBreakoutSquareIcon } from '@shared/icons/chart-breakout-square';
import { ClockRewindIcon } from '@shared/icons/clock-rewind';
import { InboxIcon } from '@shared/icons/inbox';
import { colours } from '@shared/theme/colours';
import { NotificationStatus, ResolvedNotification, ResolvedNotificationHistoryItem } from '@shared/types/notifications';

import { NotificationHistoryItem } from './components/notification-history-item';
import { NotificationItem } from './components/notification-item';
import { styles } from './NotificationsPanel.styles';

const SKELETON_ROWS = [0, 1, 2];

export type NotificationsTab = 'all' | 'history' | 'invites' | 'messages';

export interface NotificationsPanelProps extends WithStyles<typeof styles> {
  activeTab: NotificationsTab;
  historyHasMore: boolean;
  historyIsLoading: boolean;
  historyItems: ResolvedNotificationHistoryItem[];
  historyScrollRef: RefObject<HTMLDivElement>;
  loading: boolean;
  notifications: ResolvedNotification[];
  onAction: (item: ResolvedNotification) => void;
  onDismiss: (item: ResolvedNotification) => void;
  onHistoryScroll: (() => void) | undefined;
  onItemClick: (item: ResolvedNotification) => void;
  onRetry: (item: ResolvedNotification) => void;
  onTabChange: (tab: NotificationsTab) => void;
  onViewAllInvites: () => void;
}

const NotificationsPanelComponent: FC<NotificationsPanelProps> = ({
  activeTab,
  classes,
  historyHasMore,
  historyIsLoading,
  historyItems,
  historyScrollRef,
  loading,
  notifications,
  onAction,
  onDismiss,
  onHistoryScroll,
  onItemClick,
  onRetry,
  onTabChange,
  onViewAllInvites,
}) => {
  const messageNotifications = useMemo(
    () => notifications.filter((notification) => notification.category === 'message'),
    [notifications]
  );

  const inviteNotifications = useMemo(
    () => notifications.filter((notification) => notification.category === 'invite'),
    [notifications]
  );

  // Tabs answer "is there anything still wanting something in here", not "how many" — the
  // list itself is the count and the nav badge keeps the single numeral. Actioned rows are
  // excluded: they sit in the list only to show their success state.
  const allTabHasOutstanding = useMemo(
    () => notifications.some((notification) => notification.status !== NotificationStatus.Actioned),
    [notifications]
  );

  const messagesHaveOutstanding = useMemo(
    () => messageNotifications.some((notification) => notification.status !== NotificationStatus.Actioned),
    [messageNotifications]
  );

  const invitesHaveOutstanding = useMemo(
    () => inviteNotifications.some((notification) => notification.status !== NotificationStatus.Actioned),
    [inviteNotifications]
  );

  const skeleton = (
    <div className={classes.list}>
      {SKELETON_ROWS.map((row) => (
        <div key={row} className={classes.skeletonRow}>
          <span className={classes.skeletonTitle} />
          <span className={classes.skeletonMessage} />
          <span className={classes.skeletonTime} />
        </div>
      ))}
    </div>
  );

  const emptyState = (icon: ReactNode, title: string, text: string) => (
    <div className={classes.empty}>
      {icon}
      <span className={classes.emptyTitle}>{title}</span>
      <span className={classes.emptyText}>{text}</span>
    </div>
  );

  const inboxIcon = <InboxIcon classes={{ root: classes.emptyIcon }} color={colours.neutral[800]} />;
  const historyIcon = <ClockRewindIcon classes={{ root: classes.emptyIcon }} color={colours.neutral[800]} />;
  const invitesIcon = <ChartBreakoutSquareIcon classes={{ root: classes.emptyIcon }} color={colours.neutral[800]} />;

  const generalRow = (notification: ResolvedNotification) => (
    <NotificationItem
      key={notification.id}
      notification={notification}
      onAction={onAction}
      onClick={onItemClick}
      onDismiss={onDismiss}
      onRetry={onRetry}
    />
  );

  const allContent =
    notifications.length === 0 ? (
      emptyState(
        <BellIcon classes={{ root: classes.emptyIcon }} color={colours.neutral[800]} />,
        t('youre_all_caught_up'),
        t('no_new_notifications_right_now')
      )
    ) : (
      <div className={classes.list}>{notifications.map(generalRow)}</div>
    );

  const messagesContent =
    messageNotifications.length === 0 ? (
      emptyState(inboxIcon, t('no_messages'), t('new_messages_will_appear_here'))
    ) : (
      <div className={classes.list}>{messageNotifications.map(generalRow)}</div>
    );

  const invitesContent =
    inviteNotifications.length === 0 ? (
      emptyState(invitesIcon, t('no_pending_invites'), t('new_schedule_invitations_will_appear_here'))
    ) : (
      <div className={classes.list}>{inviteNotifications.map(generalRow)}</div>
    );

  const historyContent =
    historyIsLoading && historyItems.length === 0 ? (
      skeleton
    ) : historyItems.length === 0 ? (
      emptyState(historyIcon, t('no_history'), t('actioned_and_dismissed_notifications_will_appear_here'))
    ) : (
      <div className={classes.list}>
        {historyItems.map((item) => (
          <NotificationHistoryItem key={item.id} notification={item} />
        ))}
        {historyHasMore && (
          <div className={classes.loadMoreContainer}>
            <Loading size={20} />
          </div>
        )}
      </div>
    );

  // Only the active tab's content is rendered - switching tabs unmounts the previous
  // one rather than hiding it, so each tab starts fresh (bar History's own lazy-loaded state,
  // which lives with whoever fetches it).
  let bodyContent: ReactNode;

  if (loading) {
    bodyContent = <div className={classes.tabPanel}>{skeleton}</div>;
  } else {
    let activeContent: ReactNode;

    if (activeTab === 'history') {
      activeContent = historyContent;
    } else if (activeTab === 'messages') {
      activeContent = messagesContent;
    } else if (activeTab === 'invites') {
      activeContent = invitesContent;
    } else {
      activeContent = allContent;
    }

    bodyContent = (
      <div
        ref={activeTab === 'history' ? historyScrollRef : undefined}
        className={classes.tabPanel}
        onScroll={activeTab === 'history' ? onHistoryScroll : undefined}
      >
        {activeContent}
      </div>
    );
  }

  // Every tab gets the same footer shape — one note — so the panel reads consistently. The
  // slot is always rendered, which is also what keeps the panel height identical between tabs.
  let footerNote: ReactNode;

  if (activeTab === 'history') {
    footerNote = t('recent_history');
  } else if (activeTab === 'messages') {
    // No inbox to link to yet, so the slot carries a note only. Messages do not age out:
    // an unread message is still worth reading a month later.
    footerNote = t('messages_stay_here_until_you_dismiss_them');
  } else if (activeTab === 'invites') {
    footerNote = t('invites_stay_here_until_you_respond');
  } else {
    footerNote = t('notifications_stay_here_until_you_take_action_or_dismiss_them');
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <span className={classes.headerTitle}>{t('notifications')}</span>
      </div>

      <div className={classes.tabs}>
        <button
          className={cx(classes.tab, { [classes.tabActive]: activeTab === 'all' })}
          type="button"
          onClick={() => onTabChange('all')}
        >
          {t('all')}
          {allTabHasOutstanding && <span className={classes.tabDot} />}
        </button>
        <button
          className={cx(classes.tab, { [classes.tabActive]: activeTab === 'invites' })}
          type="button"
          onClick={() => onTabChange('invites')}
        >
          {t('invites')}
          {invitesHaveOutstanding && <span className={classes.tabDot} />}
        </button>
        <button
          className={cx(classes.tab, { [classes.tabActive]: activeTab === 'messages' })}
          type="button"
          onClick={() => onTabChange('messages')}
        >
          {t('messages')}
          {messagesHaveOutstanding && <span className={classes.tabDot} />}
        </button>
        <button
          className={cx(classes.tab, { [classes.tabActive]: activeTab === 'history' })}
          type="button"
          onClick={() => onTabChange('history')}
        >
          {t('history')}
        </button>
      </div>
      <div className={classes.divider} />

      <div className={classes.body}>{bodyContent}</div>

      <div className={classes.divider} />
      <div className={classes.footer}>
        {activeTab === 'invites' && (
          <button className={classes.footerLink} type="button" onClick={onViewAllInvites}>
            {t('view_all_schedule_invitations')}
            <ArrowRightIcon color="currentColor" />
          </button>
        )}
        <span className={classes.footerNote}>{footerNote}</span>
      </div>
    </div>
  );
};

export const NotificationsPanel = withStyles(styles)(NotificationsPanelComponent);
